'use client';
import { useEffect, useTransition, useRef } from 'react';
import { MyEquipmentWrapperProps } from 'services/equipment/types';
import { Conditional, useBreakpoint } from 'components/pcc-components';
import { useMyEquipmentContext } from 'components/my-equipment/my-equipment-search-pagination-context';
import MyEquipmentGrid from '../my-equipment-grid';
import MyEquipmentGaTracking from '../my-equipment-ga-tracking';
import SingleEquipmentWidget from '../single-equipment-widget';
import { SearchNoEquipment } from '../search-no-equipment';
import MyEquipmentSearchContainer from '../my-equipment-search-container';
import { MAIN_CAT_HEADER_ID_MOBILE } from '@cat-ecom/pcc-components/src/components/header/constants';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { MYEQUIPMENT_GUEST_BANNER } from 'components/my-equipment/constants';
import styles from './my-equipment-wrapper.module.scss';

const MyEquipmentWrapper = ({
  featureFlags,
  selectedEquipmentIndex,
  myEquipmentList,
  userDetail,
  serviceInsightsDetails
}: Readonly<MyEquipmentWrapperProps>) => {
  const [isPending, startTransition] = useTransition();
  const { paginatedEquipmentList, clearSelection } = useMyEquipmentContext();
  const { PCC_HEADLESS_REQUEST_SERVICE_FLAG } = featureFlags;
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const onscrollRef = useRef(null);

  useEffect(() => {
    if (!isMD && onscrollRef?.current && !userDetail.isLoggedIn) {
      const ScrollToTop =
        document
          ?.getElementById(MAIN_CAT_HEADER_ID_MOBILE)
          ?.getBoundingClientRect()?.height +
        document
          ?.getElementById(MYEQUIPMENT_GUEST_BANNER)
          ?.getBoundingClientRect()?.height;
      window.scrollTo({ top: ScrollToTop, behavior: 'smooth' });
    }
  }, [isMD, myEquipmentList, userDetail.isLoggedIn]);

  const transitionCallBack = (callBack) => {
    startTransition(callBack);
    clearSelection();
  };

  return (
    <section className={styles['equipment-container']} ref={onscrollRef}>
      {myEquipmentList?.length > 1 && <MyEquipmentSearchContainer />}
      {myEquipmentList.length === 1 ? (
        <SingleEquipmentWidget
          equipment={myEquipmentList[0]}
          userDetail={userDetail}
          serviceInsightsDetails={serviceInsightsDetails}
          isRequestServiceFlag={PCC_HEADLESS_REQUEST_SERVICE_FLAG}
          startTransition={transitionCallBack}
        />
      ) : (
        <>
          <MyEquipmentGaTracking />
          <Conditional
            test={paginatedEquipmentList.length !== 0}
            fallback={<SearchNoEquipment />}
          >
            <MyEquipmentGrid
              selectedEquipment={myEquipmentList?.[selectedEquipmentIndex]}
              isRequestServiceFlag={PCC_HEADLESS_REQUEST_SERVICE_FLAG}
              userDetail={userDetail}
              serviceInsightsDetails={serviceInsightsDetails}
              startTransition={transitionCallBack}
              isPending={isPending}
            />
          </Conditional>
        </>
      )}
    </section>
  );
};

export default MyEquipmentWrapper;
